import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";
import { usePortfolioDateFormatter } from "../../utils/usePortfolioDateFormatter";

const HEIGHT = 400;

const margin = {
  top: 20,
  right: 80,
  bottom: 20,
  left: 60
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 20;
// Multiplicative step used by the keyboard +/- zoom controls.
const ZOOM_STEP = 1.5;
// One step of the keyboard navigation, in data-point indices (5 min each).
// Shift+Arrow multiplies this up to a whole hour (12 * 5 min = 60 min).
const HOUR_STEP = 12;
// Empirically chosen so the 8-line tooltip rendered with ChartTooltip's
// translate(-50%, -100%) appears visually centered on the chart's vertical
// midline (instead of sitting with its bottom edge on the midline).
const TOOLTIP_CENTER_OFFSET = 70;

const legendSwatchLine = (color, dashed) => (
  <svg width={28} height={10} aria-hidden style={{ flexShrink: 0 }}>
    <line
      x1={2}
      y1={5}
      x2={26}
      y2={5}
      stroke={color}
      strokeWidth={2}
      strokeDasharray={dashed ? "4 4" : undefined}
    />
  </svg>
);

const legendItems = [
  { label: "Solar Actual", color: "#e67e22", dashed: false },
  { label: "Solar Target", color: "#e67e22", dashed: true },
  { label: "Battery Actual", color: "green", dashed: false },
  { label: "Battery Target", color: "green", dashed: true },
  { label: "POI Meter", color: "#2e86de", dashed: false },
  { label: "POI Limit", color: "#2e86de", dashed: true },
  { label: "Irradiance", color: "#999", dashed: false },
];

const legendSwatchLineHtml = (color, dashed) => `
  <svg width="28" height="10" aria-hidden="true" style="flex-shrink:0">
    <line x1="2" y1="5" x2="26" y2="5" stroke="${color}" stroke-width="2"${
      dashed ? ' stroke-dasharray="4 4"' : ""
    } />
  </svg>
`;

const tooltipRows = [
  { key: "solarActual", label: "Solar Actual", color: "#e67e22", dashed: false, unit: "MW" },
  { key: "solarTarget", label: "Solar Target", color: "#e67e22", dashed: true, unit: "MW" },
  { key: "batteryActual", label: "Battery Actual", color: "green", dashed: false, unit: "MW" },
  { key: "batteryTarget", label: "Battery Target", color: "green", dashed: true, unit: "MW" },
  { key: "poiActual", label: "POI Meter", color: "#2e86de", dashed: false, unit: "MW" },
  { key: "poiLimit", label: "POI Limit", color: "#2e86de", dashed: true, unit: "MW" },
  { key: "irradiance", label: "Irradiance", color: "#999", dashed: false, unit: "W/m²" },
];

const tooltipRowStyle = { display: "flex", alignItems: "center", gap: 6 };

const lineConfigs = [
  ["solarTarget", "#e67e22", "4,4"],
  ["solarActual", "#e67e22", null],
  ["batteryTarget", "green", "4,4"],
  ["batteryActual", "green", null],
  ["poiLimit", "#2e86de", "4,4"],
  ["poiActual", "#2e86de", null]
];

// Pick x-axis tick steps (in hours) so labels never crowd as the user zooms.
// `minor` is the short tick spacing; `label` is the spacing for labelled ticks.
function pickTickConfig(visibleSpan) {
  if (visibleSpan > 16) return { minor: 1, label: 3 };
  if (visibleSpan > 8) return { minor: 1, label: 2 };
  if (visibleSpan > 4) return { minor: 0.5, label: 1 };
  if (visibleSpan > 2) return { minor: 0.25, label: 0.5 };
  return { minor: 1 / 12, label: 0.25 };
}

function formatClock(hours) {
  const totalMinutes = Math.round(hours * 60);
  const hh = String(Math.floor(totalMinutes / 60) % 24).padStart(2, "0");
  const mm = String(((totalMinutes % 60) + 60) % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * Remap Snowflake UTC day-fraction/`bucketStartUtc` into plant-local clock hours
 * for the [0, 24) x-axis. Falls back to the API `t` when no timestamp is present
 * (placeholder series).
 */
function toPlantLocalT(point, dateFormatter) {
  if (!point?.bucketStartUtc || !dateFormatter) {
    return point;
  }
  const parts = dateFormatter.getDateParts(point.bucketStartUtc);
  if (!parts) {
    return point;
  }
  const hour = Number.isFinite(parts.hour) ? parts.hour % 24 : 0;
  const minute = Number.isFinite(parts.minute) ? parts.minute : 0;
  return {
    ...point,
    t: hour + minute / 60,
  };
}

// Site-output buckets are 5 minutes (1/12 h). Allow a little float slack.
const MAX_T_GAP_HOURS = 10 / 60;

/**
 * Insert `null` breaks wherever consecutive points don't advance smoothly on
 * `t` (time-of-day).
 *
 * The API returns one plant-local day in absolute-time order, but the chart
 * plots `t` on a linear [0, 24) axis. A telemetry hole (or the repeated hour of
 * a DST fall-back day) would otherwise be drawn as a straight bridge across the
 * gap. Paired with `d3.line().defined()`, these nulls break the path instead —
 * leaving the rest of the day intact.
 */
function withGapBreaks(points) {
  const out = [];
  for (let i = 0; i < points.length; i++) {
    if (i > 0) {
      const dt = Number(points[i].t) - Number(points[i - 1].t);
      if (!(dt > 0 && dt <= MAX_T_GAP_HOURS)) {
        out.push(null);
      }
    }
    out.push(points[i]);
  }
  return out;
}

export default function SiteOutputMonitor({ portfolioName, onTooltip, day = 'today' }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [series, setSeries] = useState(null);
  const dateFormatter = usePortfolioDateFormatter({ portfolioName });

  // Persist zoom transform + click/keyboard selection across resizes and data
  // refreshes so the user's view and pick survive re-renders.
  const transformRef = useRef(d3.zoomIdentity);
  const selectedIdxRef = useRef(null);
  // Read the latest tooltip setter at event time so a new callback identity
  // from the parent doesn't tear down the draw effect mid-interaction.
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;

  useEffect(() => {
    if (!portfolioName) {
      setSeries(null);
      return;
    }

    let cancelled = false;
    const duration = day === 'yesterday' ? 'yesterday' : 'today';

    (async () => {
      try {
        const raw = await apiFetch(`/api/graph/portfolio/${encodeURIComponent(portfolioName)}/monitoring/${duration}/siteoutputmonitor`);
        if (cancelled) return;
        setSeries(
          Array.isArray(raw) ? raw.map((point) => toPlantLocalT(point, dateFormatter)) : [],
        );
      } catch (err) {
        console.warn("Site output monitor API failed:", err);
        if (!cancelled) {
          setSeries([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [portfolioName, day, dateFormatter]);

  useEffect(() => {
    if (!containerRef.current || !series?.length) return;

    const data = series;

    /* =====================
       TOOLTIP fallback (only when page-level ChartTooltip isn't used)
       ===================== */
    const tooltipDiv = onTooltipRef.current
      ? null
      : d3
          .select("body")
          .append("div")
          .style("position", "absolute")
          .style("background", "white")
          .style("border", "1px solid #ccc")
          .style("padding", "6px")
          .style("border-radius", "4px")
          .style("pointer-events", "none")
          .style("box-shadow", "0 0 10px rgba(0,0,0,0.25)")
          .style("opacity", 0);

    // Bridge ref so the effect-scope keyboard/focus listeners (which outlive a
    // single draw) can always drive the most recent chart instance. Populated
    // at the end of every `draw()` call.
    const handlersRef = { current: null };

    function draw() {
      if (!containerRef.current || !svgRef.current) return;
      const width = containerRef.current.clientWidth;
      const innerW = width - margin.left - margin.right;
      const innerH = HEIGHT - margin.top - margin.bottom;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      // Clip path so lines & area stay inside the chart area when zoomed.
      svg
        .append("defs")
        .append("clipPath")
        .attr("id", "site-output-chart-clip")
        .append("rect")
        .attr("width", innerW)
        .attr("height", innerH);

      const g = svg.append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      /* =====================
         BASE SCALES
         (xBase is rescaled by the zoom transform; the y scales never change
         because this chart only zooms horizontally.)
         ===================== */
      const lastIdx = Math.max(0, data.length - 1);
      // `data` stays dense so hover / keyboard indices map 1:1 to points; only
      // the drawn paths carry the null gap markers.
      const pathData = withGapBreaks(data);

      // If the dataset shrank under us, clamp any persisted selection so it
      // can't point past the new tail.
      if (selectedIdxRef.current != null) {
        selectedIdxRef.current = Math.max(0, Math.min(lastIdx, selectedIdxRef.current));
      }

      const xBase = d3.scaleLinear()
        .domain([0, 24])
        .range([0, innerW]);

      const yMW = d3.scaleLinear()
        .domain([-200, 1000])
        .range([innerH, 0]);

      const yIrr = d3.scaleLinear()
        .domain([0, 1000])
        .range([innerH, 0]);

      /* =====================
         FIXED Y GRID & AXES (independent of zoom)
         ===================== */
      g.append("g")
        .call(
          d3.axisLeft(yMW)
            .tickValues([0, 500, 1000])
            .tickSize(-innerW)
            .tickFormat("")
        )
        .selectAll("line")
        .attr("stroke", "#ddd");

      g.append("g")
        .call(
          d3.axisLeft(yMW)
            .tickValues([0, 500, 1000])
            .tickFormat(d => d === 0 ? "0 MW" : `${d} MW`)
        );

      g.append("g")
        .attr("transform", `translate(${innerW},0)`)
        .call(
          d3.axisRight(yIrr)
            .ticks(4)
            .tickFormat(d => `${d} W/m²`)
        );

      /* =====================
         LAYER GROUPS
         ===================== */
      const axisXMinorG = g.append("g")
        .attr("class", "axis axis-x-minor")
        .attr("transform", `translate(0,${innerH})`);
      const axisXLabelG = g.append("g")
        .attr("class", "axis axis-x-label")
        .attr("transform", `translate(0,${innerH})`);

      const plotArea = g.append("g")
        .attr("class", "plot-area")
        .attr("clip-path", "url(#site-output-chart-clip)");

      const irradiancePath = plotArea.append("path")
        .attr("fill", "#ccc")
        .attr("opacity", 0.8);

      const linePaths = lineConfigs.map(([, color, dash]) =>
        plotArea.append("path")
          .attr("fill", "none")
          .attr("stroke", color)
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", dash || null)
      );

      /* =====================
         CROSSHAIR + INTERACTION OVERLAY
         ===================== */
      const crosshair = g.append("line")
        .attr("stroke", "#666")
        .attr("y1", 0)
        .attr("y2", innerH)
        .style("pointer-events", "none")
        .style("opacity", 0);

      const overlay = g.append("rect")
        .attr("class", "interaction-overlay")
        .attr("width", innerW)
        .attr("height", innerH)
        .attr("fill", "transparent")
        .style("cursor", "crosshair");

      /* =====================
         ZOOM-DRIVEN RENDER
         ===================== */
      let xScale = xBase;
      const yScale = yMW;

      function applyTransform(transform) {
        transformRef.current = transform;
        xScale = transform.rescaleX(xBase);

        const [xMin, xMax] = xScale.domain();
        const visibleSpan = Math.max(0, xMax - xMin);
        const { minor, label } = pickTickConfig(visibleSpan);

        const lo = Math.max(0, xMin);
        const hi = Math.min(24, xMax);

        const minorStart = Math.ceil(lo / minor) * minor;
        const minorTicks =
          hi >= minorStart ? d3.range(minorStart, hi + 1e-9, minor) : [];
        const labelStart = Math.ceil(lo / label) * label;
        const labelTicks =
          hi >= labelStart ? d3.range(labelStart, hi + 1e-9, label) : [];

        const isLabelTick = (v) =>
          labelTicks.some((l) => Math.abs(l - v) < 1e-6);
        const isMajorTick = (v) => {
          const span = label * 2;
          return Math.abs(v / span - Math.round(v / span)) < 1e-6;
        };

        axisXMinorG
          .call(
            d3.axisBottom(xScale)
              .tickValues(minorTicks)
              .tickFormat("")
          );
        axisXMinorG.selectAll(".tick line")
          .attr("y2", (v) =>
            isMajorTick(v) ? -24 : isLabelTick(v) ? -12 : -6
          );
        axisXMinorG.select(".domain").remove();

        axisXLabelG
          .call(
            d3.axisBottom(xScale)
              .tickValues(labelTicks)
              .tickFormat((v) => formatClock(v))
          );
        axisXLabelG.selectAll(".tick line").remove();
        axisXLabelG.select(".domain").remove();

        const area = d3.area()
          .defined(d => d != null)
          .x(d => xScale(d.t))
          .y0(yIrr(0))
          .y1(d => yIrr(d.irradiance));

        irradiancePath.datum(pathData).attr("d", area);

        lineConfigs.forEach(([key], i) => {
          const lineGen = d3.line()
            .defined(d => d != null)
            .x(d => xScale(d.t))
            .y(d => yScale(d[key]));
          linePaths[i].datum(pathData).attr("d", lineGen);
        });
      }

      /* =====================
         TOOLTIP / HOVER HANDLERS
         ===================== */
      let isDragging = false;
      // Mouse hover index for this draw cycle. The persistent click/keyboard
      // selection lives on `selectedIdxRef` (component scope).
      let hoveredIdx = null;

      const cursorForZoom = (k) => (k > 1 ? "grab" : "crosshair");

      const hideTooltip = () => {
        if (onTooltipRef.current) {
          onTooltipRef.current((t) => ({ ...t, visible: false }));
        } else {
          tooltipDiv?.style("opacity", 0);
        }
      };

      const hideCrosshair = () => {
        crosshair.style("opacity", 0);
      };

      // Find the data index whose `t` is closest to a given hour value.
      function nearestIdx(hour) {
        let best = 0;
        let bestDist = Infinity;
        for (let i = 0; i < data.length; i++) {
          const dist = Math.abs(data[i].t - hour);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        }
        return best;
      }

      // Mouse hover wins when present; otherwise the persistent keyboard /
      // click selection drives the indicator while the SVG is focused.
      function activeIdx() {
        if (hoveredIdx != null) return hoveredIdx;
        if (
          selectedIdxRef.current != null &&
          svgRef.current &&
          document.activeElement === svgRef.current
        ) {
          return selectedIdxRef.current;
        }
        return null;
      }

      // Render the crosshair + tooltip for a snapped data-point index.
      // - x snaps to the data point (so hover/keyboard share one geometry).
      // - y is pinned to the chart's vertical center, so the tooltip never
      //   bobs up and down with cursor movement.
      // - Indices outside the (zoomed) visible range hide everything.
      function showAtIdx(idx) {
        if (idx == null || idx < 0 || idx > lastIdx) {
          hideCrosshair();
          hideTooltip();
          return;
        }
        const d = data[idx];
        if (!d) {
          hideCrosshair();
          hideTooltip();
          return;
        }

        const cx = xScale(d.t);
        if (cx < 0 || cx > innerW) {
          hideCrosshair();
          hideTooltip();
          return;
        }

        crosshair.attr("x1", cx).attr("x2", cx).style("opacity", 1);

        const time = formatClock(d.t);
        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();

        const tooltipX = rect.left + margin.left + cx;
        const tooltipY =
          rect.top + margin.top + innerH / 2 + TOOLTIP_CENTER_OFFSET;

        if (onTooltipRef.current) {
          onTooltipRef.current({
            visible: true,
            x: tooltipX,
            y: tooltipY,
            content: (
              <>
                <div>
                  <strong>Time:</strong> {time}
                </div>
                {tooltipRows.map(({ key, label, color, dashed, unit }) => (
                  <div key={key} style={tooltipRowStyle}>
                    {legendSwatchLine(color, dashed)}
                    <span>
                      <strong>{label}:</strong> {d[key].toFixed(1)} {unit}
                    </span>
                  </div>
                ))}
              </>
            ),
          });
        } else {
          tooltipDiv
            ?.style("opacity", 1)
            .html(`
              <div><strong>Time:</strong> ${time}</div>
              ${tooltipRows
                .map(
                  ({ key, label, color, dashed, unit }) => `
                    <div style="display:flex;align-items:center;gap:6px">
                      ${legendSwatchLineHtml(color, dashed)}
                      <span><strong>${label}:</strong> ${d[key].toFixed(1)} ${unit}</span>
                    </div>
                  `
                )
                .join("")}
            `)
            .style("left", `${tooltipX + window.scrollX}px`)
            .style("top", `${tooltipY + window.scrollY}px`);
        }
      }

      function refreshIndicator() {
        const idx = activeIdx();
        if (idx == null) {
          hideCrosshair();
          hideTooltip();
          return;
        }
        showAtIdx(idx);
      }

      function handleHover(mx) {
        if (lastIdx < 0) return;
        const hour = xScale.invert(mx);
        hoveredIdx = nearestIdx(hour);
        showAtIdx(hoveredIdx);
      }

      overlay
        .on("mousemove", (event) => {
          if (isDragging) return;
          const [mx] = d3.pointer(event);
          handleHover(mx);
        })
        .on("mouseleave", () => {
          if (isDragging) return;
          hoveredIdx = null;
          // Falls back to the persistent selection if the SVG still has
          // keyboard focus, otherwise hides everything.
          refreshIndicator();
        })
        .on("click", (event) => {
          if (isDragging) return;
          if (lastIdx < 0) return;
          const [mx] = d3.pointer(event);
          selectedIdxRef.current = nearestIdx(xScale.invert(mx));
          // Focus the SVG so subsequent arrow / +/- keys are captured by the
          // keyboard listeners.
          svgRef.current?.focus({ preventScroll: true });
          refreshIndicator();
        });

      /* =====================
         ZOOM + PAN (horizontal only)
         - wheel up   -> zoom in toward pointer
         - wheel down -> zoom out, clamped at the default 1x view
         - click+drag -> pan horizontally (only when zoomed in)
         - keyboard   -> +/- via `zoomBy`, anchored on the selected point
         ===================== */
      const zoom = d3.zoom()
        .scaleExtent([MIN_ZOOM, MAX_ZOOM])
        .extent([
          [0, 0],
          [innerW, innerH],
        ])
        .translateExtent([
          [0, 0],
          [innerW, innerH],
        ])
        .filter((event) => {
          if (event.type === "wheel") return true;
          if (event.type === "mousedown") {
            return transformRef.current.k > MIN_ZOOM;
          }
          return false;
        })
        .on("start", (event) => {
          if (event.sourceEvent && event.sourceEvent.type === "mousedown") {
            isDragging = false;
          }
        })
        .on("zoom", (event) => {
          applyTransform(event.transform);

          const sourceEvent = event.sourceEvent;
          if (!sourceEvent) {
            // Programmatic pan/zoom (keyboard +/- or navigate auto-pan).
            refreshIndicator();
            return;
          }

          if (sourceEvent.type === "mousemove") {
            if (!isDragging) {
              isDragging = true;
              overlay.style("cursor", "grabbing");
              hoveredIdx = null;
              hideCrosshair();
              hideTooltip();
            }
          } else if (sourceEvent.type === "wheel") {
            overlay.style("cursor", cursorForZoom(event.transform.k));
            const [mx] = d3.pointer(sourceEvent, overlay.node());
            handleHover(mx);
          }
        })
        .on("end", () => {
          if (isDragging) {
            isDragging = false;
          }
          overlay.style("cursor", cursorForZoom(transformRef.current.k));
          // After a drag-pan or wheel-zoom, the selected point may now sit
          // somewhere new on screen — re-render the persistent indicator.
          refreshIndicator();
        });

      overlay.style("cursor", cursorForZoom(transformRef.current.k));
      overlay.call(zoom);

      // Restore any prior zoom state (preserved across resizes / re-renders).
      // Setting the transform fires the "zoom" handler, which performs the
      // initial render.
      overlay.call(zoom.transform, transformRef.current);

      /* =====================
         KEYBOARD-DRIVEN PAN + ZOOM
         ===================== */

      // Center the visible viewport horizontally on the selected point. The d3
      // zoom translateExtent automatically clamps near the data start/end, so
      // the selection "unlocks" from center and slides toward the edge there.
      function centerOnSelected() {
        if (selectedIdxRef.current == null) return;
        if (transformRef.current.k <= MIN_ZOOM) return;
        const d = data[selectedIdxRef.current];
        if (!d) return;
        overlay.call(zoom.translateTo, xBase(d.t), innerH / 2);
      }

      function navigate(dx) {
        if (lastIdx < 0) return;
        let cur = selectedIdxRef.current;
        if (cur == null) {
          const [xMin, xMax] = xScale.domain();
          cur = nearestIdx((xMin + xMax) / 2);
        }
        let next = cur + dx;
        // Boundary clamp. Shift+Arrow falls back to the last data point when
        // less than an hour of data remains in the requested direction.
        if (next < 0) next = 0;
        if (next > lastIdx) next = lastIdx;
        if (next === selectedIdxRef.current) return;
        selectedIdxRef.current = next;
        centerOnSelected();
        // centerOnSelected may have been a no-op (1x zoom or clamped at the
        // boundary) — refresh explicitly so the tooltip always moves.
        refreshIndicator();
      }

      // +/- zoom anchored on the selected point's current pixel position, so
      // the selection stays under the same screen pixel before and after
      // (subject to translateExtent clamping at MIN_ZOOM).
      function zoomBy(steps) {
        const t = transformRef.current;
        const factor = Math.pow(ZOOM_STEP, steps);
        const targetK = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, t.k * factor));
        if (targetK === t.k) return;

        let anchorIdx = selectedIdxRef.current;
        if (anchorIdx == null) {
          const [xMin, xMax] = xScale.domain();
          anchorIdx = nearestIdx((xMin + xMax) / 2);
        }
        const d = data[anchorIdx];
        if (!d) return;
        const px = xScale(d.t);
        const py = innerH / 2;
        overlay
          .transition()
          .duration(150)
          .call(zoom.scaleBy, targetK / t.k, [px, py]);
      }

      // Publish this draw cycle's nav/zoom/refresh hooks so the effect-scope
      // keyboard listeners always drive the latest chart instance.
      handlersRef.current = {
        navigate,
        zoomBy,
        refresh: refreshIndicator,
        ensureDefaultSelection: () => {
          if (selectedIdxRef.current == null && lastIdx >= 0) {
            const [xMin, xMax] = xScale.domain();
            selectedIdxRef.current = nearestIdx((xMin + xMax) / 2);
          }
        },
      };

      // If the chart was already focused when this draw ran (e.g. a resize or
      // data refresh while navigating), re-render the selection so the
      // indicator persists across redraws.
      if (svgRef.current && document.activeElement === svgRef.current) {
        handlersRef.current.ensureDefaultSelection();
        refreshIndicator();
      }
    }

    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(containerRef.current);

    /* =====================
       KEYBOARD NAVIGATION
       - Tab to focus, or click on the plot to seed the selection.
       - Arrow keys move the selection by one 5-minute interval.
       - Shift+Arrow jumps a whole hour; clamps to first/last data point
         when less than an hour remains in that direction.
       - + / = / NumpadAdd zoom in toward the selected point.
       - - / _ / NumpadSubtract zoom out, keeping the selection anchored.
       Listeners live at effect scope and read from `handlersRef` so they
       always drive whichever draw cycle is currently active.
       ===================== */
    const svgEl = svgRef.current;

    const handleKeyDown = (e) => {
      const h = handlersRef.current;
      if (!h) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        h.navigate(e.shiftKey ? -HOUR_STEP : -1);
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        h.navigate(e.shiftKey ? HOUR_STEP : 1);
        return;
      }
      if (e.key === "+" || e.key === "=" || e.code === "NumpadAdd") {
        e.preventDefault();
        h.zoomBy(1);
        return;
      }
      if (e.key === "-" || e.key === "_" || e.code === "NumpadSubtract") {
        e.preventDefault();
        h.zoomBy(-1);
      }
    };

    const handleFocus = () => {
      const h = handlersRef.current;
      if (!h) return;
      h.ensureDefaultSelection();
      h.refresh();
    };

    const handleBlur = () => {
      const h = handlersRef.current;
      if (!h) return;
      // refresh() sees `document.activeElement !== svgEl` and hides the
      // selection-driven indicator + tooltip.
      h.refresh();
    };

    svgEl?.addEventListener("keydown", handleKeyDown);
    svgEl?.addEventListener("focus", handleFocus);
    svgEl?.addEventListener("blur", handleBlur);

    return () => {
      observer.disconnect();
      tooltipDiv?.remove();
      svgEl?.removeEventListener("keydown", handleKeyDown);
      svgEl?.removeEventListener("focus", handleFocus);
      svgEl?.removeEventListener("blur", handleBlur);
    };
  }, [series]);

  return (
    <div ref={containerRef} style={{ width: "100%", minWidth: 0, position: "relative" }}>
      {series && series.length === 0 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1.5rem",
            textAlign: "center",
            color: "#666",
            fontSize: 14,
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          No site output data for {day === "yesterday" ? "yesterday" : "today"}.
          The Snowflake view may not have been refreshed yet.
        </div>
      )}
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label="Site output monitor. Click or tab to focus, then use arrow keys to move the selection by 5 minutes (Shift+Arrow for one hour) and + / - to zoom around it."
        style={{ width: "100%", height: HEIGHT, display: "block" }}
      />
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px 18px",
          justifyContent: "center",
          alignItems: "center",
          padding: "10px 8px 4px",
          fontSize: 12,
          color: "#333",
        }}
      >
        {legendItems.map(({ label, color, dashed }) => (
          <span
            key={label}
            style={{ display: "inline-flex", fontWeight: "bold", alignItems: "center", gap: 6 }}
          >
            {legendSwatchLine(color, dashed)}
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
